import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Button } from "../ui";
import { useToast } from "../ui/toast";

interface Cat {
  id: string;
  name: string;
}

export function QuickCapture({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [body, setBody] = useState("");
  const [cats, setCats] = useState<Cat[]>([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setBody("");
      setTimeout(() => ref.current?.focus(), 30);
      api.get<Cat[]>("/categories").then(setCats).catch(() => setCats([]));
    }
  }, [open]);

  // Inline parse: erste Zeile "#tag #tag2 @kategorie" wird interpretiert
  const parsed = parseInline(body);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      let category_id: string | null = null;
      if (parsed.category) {
        const found = cats.find((c) => c.name.toLowerCase() === parsed.category!.toLowerCase());
        if (found) category_id = found.id;
        else {
          // Kategorie neu anlegen on the fly
          try {
            const created = await api.post<Cat>("/categories", { name: parsed.category });
            category_id = created.id;
            setCats((s) => [...s, created]);
          } catch {
            /* duplicate or other — egal, lassen wir leer */
          }
        }
      }
      await api.post("/notes", {
        body_md: parsed.body,
        tags: parsed.tags,
        category_id,
      });
      toast.ok("Notiz gespeichert", parsed.tags.length ? `#${parsed.tags.join(" #")}` : undefined);
      onClose();
    } catch (e) {
      toast.err("Fehler beim Speichern", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          className="fixed top-[18vh] left-1/2 -translate-x-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[640px] max-w-[92vw] animate-slide-up"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="sr-only">Quick-Capture</Dialog.Title>
          <textarea
            ref={ref}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKey}
            placeholder="Idee, Gedanke … Tags mit #, Kategorie mit @"
            rows={3}
            className="w-full px-4 py-3 text-base bg-transparent focus:outline-none resize-none"
          />
          <div className="border-t border-border px-4 py-2 flex items-center gap-2 text-xs text-text-2">
            {parsed.tags.length > 0 && (
              <div className="flex gap-1">
                {parsed.tags.map((t) => (
                  <span
                    key={t}
                    className="px-1.5 py-0.5 bg-brand-100 dark:bg-brand-700/30 text-brand-700 dark:text-brand-100 rounded-md text-[11px]"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
            {parsed.category && (
              <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-700/30 text-amber-700 dark:text-amber-100 rounded-md text-[11px]">
                @{parsed.category}
              </span>
            )}
            <span className="ml-auto flex items-center gap-3">
              <span>
                <kbd className="border border-border-strong rounded-md px-1">
                  ↵
                </kbd>{" "}
                speichern · <kbd className="border border-border-strong rounded-md px-1">⇧↵</kbd> neue Zeile
              </span>
              <Button
                variant="primary"
                size="sm"
                onClick={submit}
                loading={busy}
                disabled={!body.trim()}
              >
                Speichern
              </Button>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function parseInline(body: string): { body: string; tags: string[]; category: string | null } {
  const tags: string[] = [];
  let category: string | null = null;
  // Tags und Kategorien werden aus dem Text extrahiert, aber NICHT entfernt —
  // der Text bleibt menschenlesbar, die Tags werden gleichzeitig im Backend gesetzt.
  for (const m of body.matchAll(/(?:^|\s)#([\w-]{1,30})/g)) {
    tags.push(m[1].toLowerCase());
  }
  const catMatch = body.match(/(?:^|\s)@([\w-]{1,30})/);
  if (catMatch) category = catMatch[1];
  return { body, tags, category };
}
